import { DateTime } from 'luxon';

const MOCK_QUERY_PARAM = 'mockBookingStatus'
const MOCK_STORAGE_KEY = 'bookingStatus.useMockData'

const isTruthyFlag = (value?: string | null) =>
  ['1', 'true', 'yes', 'on'].includes(String(value ?? '').toLowerCase())

export const isBookingStatusMockModeEnabled = () => {
  if (isTruthyFlag(process.env.REACT_APP_BOOKING_STATUS_USE_MOCKS)) {
    return true
  }

  if (typeof window === 'undefined') {
    return false
  }

  const params = new URLSearchParams(window.location.search)
  if (isTruthyFlag(params.get(MOCK_QUERY_PARAM))) {
    return true
  }

  try {
    return isTruthyFlag(window.localStorage.getItem(MOCK_STORAGE_KEY))
  } catch {
    return false
  }
}

type MockSearchPayload = {
  status?: string[]
  search?: string
  clientName?: string
  orderBy?: {
    isDescending?: boolean
    jobOrderByTypeName?: string
  }
}

type MockSearchArgs = {
  limit: number
  offset?: number
  payload?: MockSearchPayload
}

const londonNow = () => DateTime.now().setZone('Europe/London')

const isoMinutesFromNow = (minutes: number) =>
  londonNow().plus({ minutes }).toISO()

const vehicleIcon = (name: string) =>
  `https://jyrneydocumentsdev.blob.core.windows.net/vehicle-logos-v2/${name}.png`

const createAddress = ({
  latitude,
  longitude,
  formattedAddress,
  addressLine1,
  addressLine2,
  addressLine3 = 'England',
  town,
  county,
  postCode,
}: {
  latitude: number
  longitude: number
  formattedAddress: string
  addressLine1: string
  addressLine2?: string
  addressLine3?: string
  town: string
  county: string
  postCode: string
}) => ({
  latitude,
  longitude,
  formattedAddress,
  addressLine1,
  addressLine2,
  addressLine3,
  town,
  county,
  postCode,
  country: 'United Kingdom',
  countryCode: 'GBR',
})

const createPassenger = (
  name: string,
  isLeadPassenger = false,
  contactNumber?: string,
) => ({
  isLeadPassenger,
  name,
  contactNumber,
  email: contactNumber
    ? `${name.toLowerCase().replace(/\s+/g, '.')}@example.com`
    : undefined,
})

const createDriver = (
  name: string,
  contactNumber: string,
  makeAndModel: string,
  registrationNumber: string,
  colour = 'Black',
) => ({
  name,
  contactNumber,
  vehicle: {
    colour,
    makeAndModel,
    registrationNumber,
  },
})

const createPrice = (total: number) => ({
  price: Number((total * 0.84).toFixed(2)),
  currency: 'GBP',
  vat: Number((total * 0.16).toFixed(2)),
  total,
})

const baseClient = {
  clientId: 1,
  accountNumber: '00001001',
  companyName: 'FREIGHTLINER GROUP LIMITED',
}

const clientVariants = [
  { companyName: 'FREIGHTLINER GROUP LIMITED', alias: 'Freightliner' },
  { companyName: 'TfL', alias: '' },
  {
    companyName: 'Stark Industries Executive Travel Limited',
    alias: 'Stark Travel',
  },
  { companyName: 'Veezu', alias: '' },
  {
    companyName:
      'International Premier Chauffeur & Executive Travel Services Limited',
    alias: 'Premier Chauffeur',
  },
  { companyName: 'Bolt Cars', alias: '' },
  {
    companyName: 'Wayne Enterprises Ground Transportation',
    alias: 'Wayne Logistics',
  },
  { companyName: 'Globex Corp', alias: '' },
  {
    companyName:
      'United Kingdom National Coach & Executive Travel Holdings Limited',
    alias: 'UK Coach & Exec',
  },
  { companyName: 'Northbank Cars', alias: '' },
]

const vehicleCatalog = {
  standard: {
    category: 'Taxi',
    vehicleTypeName: 'Taxi_Standard',
    displayName: 'Standard',
    seats: 4,
    luggage: 2,
    example: 'Skoda Octavia',
    icon: vehicleIcon('Standard'),
    isAmendable: true,
    isTrackable: true,
  },
  executive: {
    category: 'Taxi',
    vehicleTypeName: 'Taxi_Executive',
    displayName: 'Executive',
    seats: 4,
    luggage: 2,
    example: 'Mercedes E-Class',
    icon: vehicleIcon('Executive'),
    isAmendable: true,
    isTrackable: true,
  },
  premium: {
    category: 'Taxi',
    vehicleTypeName: 'Taxi_Premium',
    displayName: 'Premium',
    seats: 4,
    luggage: 2,
    example: 'Mercedes S-Class',
    icon: vehicleIcon('Premium'),
    isAmendable: true,
    isTrackable: true,
  },
  hybrid: {
    category: 'Taxi',
    vehicleTypeName: 'Taxi_Hybrid',
    displayName: 'Hybrid',
    seats: 4,
    luggage: 2,
    example: 'Kia Niro',
    icon: vehicleIcon('Hybrid'),
    isAmendable: true,
    isTrackable: true,
  },
  mpv: {
    category: 'Taxi',
    vehicleTypeName: 'Taxi_MPV',
    displayName: 'MPV',
    seats: 6,
    luggage: 4,
    example: 'Volkswagen Sharan',
    icon: vehicleIcon('MPV'),
    isAmendable: true,
    isTrackable: true,
  },
  untrackedPremium: {
    category: 'Taxi',
    vehicleTypeName: 'Taxi_Premium',
    displayName: 'Premium',
    seats: 4,
    luggage: 2,
    example: 'Mercedes S-Class',
    icon: vehicleIcon('Premium'),
    isAmendable: true,
    isTrackable: false,
  },
}

const createJob = (overrides: any) => {
  const id = overrides.id
  const bookingId = overrides.bookingId
  const defaultClient = clientVariants[id % clientVariants.length]

  const baseJob = {
    id,
    bookingId,
    associatedBookings: [],
    bookingReference: `B${String(bookingId).padStart(8, '0')}`,
    journeyReference: `C${String(bookingId).padStart(8, '0')}`,
    bookingTypeName: 'Taxi',
    journeyTypeName: 'OneWay',
    isBackup: false,
    isImmediateJourney: false,
    when: isoMinutesFromNow(60),
    timeZone: 'Europe/London',
    estimatedArrivalTime: 25,
    jobReference: `J${String(id).padStart(8, '0')}`,
    client: {
      ...baseClient,
      companyName: defaultClient.companyName,
      alias: defaultClient.alias,
    },
    supplier: {
      supplierId: 246,
      companyName: 'GNET - Sandbox',
      supplierReference: `63908379${id}`,
      isDestinationLogicSupplier: false,
      isPlatformSupplier: false,
      contactNumber: '+442071234567',
    },
    passengers: [createPassenger('Alex Morgan', true, '+447700900001')],
    pickup: createAddress({
      latitude: 51.5154,
      longitude: -0.141,
      formattedAddress: '10 Hanover Square, London, W1S 1JH, United Kingdom',
      addressLine1: '10 Hanover Square',
      addressLine3: 'Mayfair, England',
      town: 'London',
      county: 'London',
      postCode: 'W1S 1JH',
    }),
    dropOff: createAddress({
      latitude: 51.5033,
      longitude: -0.1195,
      formattedAddress: 'Waterloo Station, London, SE1 8SW, United Kingdom',
      addressLine1: 'Waterloo Station',
      addressLine3: 'Waterloo, England',
      town: 'London',
      county: 'London',
      postCode: 'SE1 8SW',
    }),
    additionalStops: [],
    where: undefined,
    vehicleDetails: vehicleCatalog.standard,
    status: {
      progress: 'Confirmed',
      status: 'Not_Dispatched',
      ragStatus: 'Flashing',
      estimatedArrivalTime: 0,
      isOutOfHours: false,
    },
    services: [],
    estimatedPrice: createPrice(38),
    driver: undefined,
    flightDetails: undefined,
    booker: {
      firstName: 'Operations',
      lastName: 'Desk',
      contactNumber: '+442079460000',
    },
    cancellationPolicy:
      'If you cancel after the driver has been dispatched, cancellation fees may apply.',
    noShowPolicy: 'No-show fees may apply if the passenger is unreachable.',
    amendPolicy:
      'You can amend until the driver is on the way. After that, cancel and rebook may be required.',
    href: `https://api-dev.jyrney.com/bookings-api/Jobs/${id}`,
  }

  return {
    ...baseJob,
    ...overrides,
    associatedBookings:
      overrides.associatedBookings ?? baseJob.associatedBookings,
    client: {
      ...baseJob.client,
      ...overrides.client,
    },
    supplier: {
      ...baseJob.supplier,
      ...overrides.supplier,
    },
    pickup: {
      ...baseJob.pickup,
      ...overrides.pickup,
    },
    dropOff: {
      ...baseJob.dropOff,
      ...overrides.dropOff,
    },
    status: {
      ...baseJob.status,
      ...overrides.status,
    },
    vehicleDetails: {
      ...baseJob.vehicleDetails,
      ...overrides.vehicleDetails,
    },
    estimatedPrice: {
      ...baseJob.estimatedPrice,
      ...overrides.estimatedPrice,
    },
    passengers: overrides.passengers ?? baseJob.passengers,
    services: overrides.services ?? baseJob.services,
    additionalStops: overrides.additionalStops ?? baseJob.additionalStops,
    booker: {
      ...baseJob.booker,
      ...overrides.booker,
    },
    driver: overrides.driver ?? baseJob.driver,
    flightDetails: overrides.flightDetails ?? baseJob.flightDetails,
  }
}

const mockJobs = [
  createJob({
    id: 9101,
    bookingId: 6201,
    jobReference: 'J00009101',
    bookingReference: 'B00046201',
    journeyReference: 'C00046201',
    when: isoMinutesFromNow(-20),
    estimatedArrivalTime: 35,
    isImmediateJourney: true,
    passengers: [
      createPassenger('Amelia Carter', true, '+447700900101'),
      createPassenger('Ben Carter'),
    ],
    pickup: createAddress({
      latitude: 51.5108,
      longitude: -0.1346,
      formattedAddress:
        'St Martin-in-the-Fields, London, WC2N 4JJ, United Kingdom',
      addressLine1: 'St Martin-in-the-Fields',
      addressLine3: 'Covent Garden, England',
      town: 'London',
      county: 'London',
      postCode: 'WC2N 4JJ',
    }),
    dropOff: createAddress({
      latitude: 51.5007,
      longitude: -0.1246,
      formattedAddress:
        'Westminster Bridge Road, London, SE1 7PB, United Kingdom',
      addressLine1: 'Westminster Bridge Road',
      addressLine3: 'South Bank, England',
      town: 'London',
      county: 'London',
      postCode: 'SE1 7PB',
    }),
    where: {
      latitude: 51.5067,
      longitude: -0.1289,
    },
    vehicleDetails: vehicleCatalog.executive,
    driver: createDriver(
      'Darren Shah',
      '+447700900201',
      'Mercedes E-Class',
      'LX22 ABC',
      'Silver',
    ),
    status: {
      progress: 'OnRoute',
      status: 'Delayed',
      ragStatus: 'Amber',
      estimatedArrivalTime: 3000,
      statusNote: 'Heavy traffic on Whitehall is pushing ETA out.',
    },
    estimatedPrice: createPrice(52),
  }),
  createJob({
    id: 9102,
    bookingId: 6202,
    jobReference: 'J00009102',
    bookingReference: 'B00046202',
    journeyReference: 'C00046202',
    when: isoMinutesFromNow(-15),
    estimatedArrivalTime: 30,
    passengers: [createPassenger('Noah Patel', true, '+447700900102')],
    pickup: createAddress({
      latitude: 51.5175,
      longitude: -0.127,
      formattedAddress: 'Russell Square, London, WC1B 5BE, United Kingdom',
      addressLine1: 'Russell Square',
      addressLine3: 'Bloomsbury, England',
      town: 'London',
      county: 'London',
      postCode: 'WC1B 5BE',
    }),
    dropOff: createAddress({
      latitude: 51.5136,
      longitude: -0.089,
      formattedAddress: '30 St Mary Axe, London, EC3A 8BF, United Kingdom',
      addressLine1: '30 St Mary Axe',
      addressLine3: 'City of London, England',
      town: 'London',
      county: 'London',
      postCode: 'EC3A 8BF',
    }),
    where: {
      latitude: 51.5154,
      longitude: -0.1087,
    },
    vehicleDetails: vehicleCatalog.hybrid,
    driver: createDriver(
      'Priya Malhotra',
      '+447700900202',
      'Kia Niro',
      'HY24 ETA',
      'Blue',
    ),
    status: {
      progress: 'OnRoute',
      status: 'Ontime',
      ragStatus: 'Green',
      estimatedArrivalTime: 900,
    },
    estimatedPrice: createPrice(44),
  }),
  createJob({
    id: 9103,
    bookingId: 6203,
    jobReference: 'J00009103',
    bookingReference: 'B00046203',
    journeyReference: 'C00046203',
    when: isoMinutesFromNow(5),
    estimatedArrivalTime: 25,
    passengers: [
      createPassenger('Charlotte Green', true, '+447700900103'),
      createPassenger('Henry Green'),
    ],
    pickup: createAddress({
      latitude: 51.4975,
      longitude: -0.1357,
      formattedAddress: 'Victoria Station, London, SW1V 1JT, United Kingdom',
      addressLine1: 'Victoria Station',
      addressLine3: 'Victoria, England',
      town: 'London',
      county: 'London',
      postCode: 'SW1V 1JT',
    }),
    dropOff: createAddress({
      latitude: 51.47,
      longitude: -0.4543,
      formattedAddress: 'Terminal 5, Heathrow Airport, TW6 2GA, United Kingdom',
      addressLine1: 'Terminal 5',
      addressLine3: 'Heathrow, England',
      town: 'Hounslow',
      county: 'Greater London',
      postCode: 'TW6 2GA',
    }),
    where: {
      latitude: 51.4962,
      longitude: -0.1415,
    },
    vehicleDetails: vehicleCatalog.premium,
    driver: createDriver(
      'Lucas Hammond',
      '+447700900203',
      'Mercedes S-Class',
      'SV73 VIP',
      'Black',
    ),
    status: {
      progress: 'Driver_Allocated',
      status: 'Ontime',
      ragStatus: 'Green',
      estimatedArrivalTime: 0,
      statusNote: 'Driver has accepted and is heading to the pickup.',
    },
    estimatedPrice: createPrice(88),
  }),
  createJob({
    id: 9104,
    bookingId: 6204,
    jobReference: 'J00009104',
    bookingReference: 'B00046204',
    journeyReference: 'C00046204',
    when: isoMinutesFromNow(-2),
    estimatedArrivalTime: 18,
    passengers: [createPassenger('Daniel Reed', true, '+447700900104')],
    pickup: createAddress({
      latitude: 51.5152,
      longitude: -0.0722,
      formattedAddress:
        'Liverpool Street Station, London, EC2M 7QA, United Kingdom',
      addressLine1: 'Liverpool Street Station',
      addressLine3: 'City of London, England',
      town: 'London',
      county: 'London',
      postCode: 'EC2M 7QA',
    }),
    dropOff: createAddress({
      latitude: 51.5072,
      longitude: -0.1657,
      formattedAddress: 'Paddington Station, London, W2 1HQ, United Kingdom',
      addressLine1: 'Paddington Station',
      addressLine3: 'Paddington, England',
      town: 'London',
      county: 'London',
      postCode: 'W2 1HQ',
    }),
    where: {
      latitude: 51.5152,
      longitude: -0.0722,
    },
    vehicleDetails: vehicleCatalog.standard,
    driver: createDriver(
      'Mason Cole',
      '+447700900204',
      'Skoda Octavia',
      'ST70 CAB',
      'Grey',
    ),
    status: {
      progress: 'Arrived',
      status: 'Driver_Waiting',
      ragStatus: 'Green',
      estimatedArrivalTime: 0,
    },
    estimatedPrice: createPrice(36),
  }),
  createJob({
    id: 9105,
    bookingId: 6205,
    jobReference: 'J00009105',
    bookingReference: 'B00046205',
    journeyReference: 'C00046205',
    when: isoMinutesFromNow(-8),
    estimatedArrivalTime: 22,
    passengers: [
      createPassenger('Isla Nguyen', true, '+447700900105'),
      createPassenger('Mia Nguyen'),
    ],
    pickup: createAddress({
      latitude: 51.5299,
      longitude: -0.1257,
      formattedAddress: 'Kings Cross, London, N1C 4AP, United Kingdom',
      addressLine1: 'Kings Cross',
      addressLine3: 'Kings Cross, England',
      town: 'London',
      county: 'London',
      postCode: 'N1C 4AP',
    }),
    dropOff: createAddress({
      latitude: 51.5194,
      longitude: -0.175,
      formattedAddress: 'St Marys Hospital, London, W2 1NY, United Kingdom',
      addressLine1: 'St Marys Hospital',
      addressLine3: 'Paddington, England',
      town: 'London',
      county: 'London',
      postCode: 'W2 1NY',
    }),
    where: {
      latitude: 51.5232,
      longitude: -0.1511,
    },
    vehicleDetails: vehicleCatalog.executive,
    driver: createDriver(
      'Sanjay Kapoor',
      '+447700900205',
      'Mercedes E-Class',
      'EK21 CAB',
      'Black',
    ),
    status: {
      progress: 'Passenger_Picked_Up',
      status: 'In_Progress',
      ragStatus: 'Green',
      estimatedArrivalTime: 720,
    },
    estimatedPrice: createPrice(49),
  }),
  createJob({
    id: 9106,
    bookingId: 6206,
    jobReference: 'J00009106',
    bookingReference: 'B00046206',
    journeyReference: 'C00046206',
    when: isoMinutesFromNow(-45),
    estimatedArrivalTime: 28,
    passengers: [createPassenger('Ethan Ross', true, '+447700900106')],
    pickup: createAddress({
      latitude: 51.4907,
      longitude: -0.1465,
      formattedAddress: 'Sloane Square, London, SW1W 8BB, United Kingdom',
      addressLine1: 'Sloane Square',
      addressLine3: 'Chelsea, England',
      town: 'London',
      county: 'London',
      postCode: 'SW1W 8BB',
    }),
    dropOff: createAddress({
      latitude: 51.5151,
      longitude: -0.1419,
      formattedAddress: 'Oxford Circus, London, W1B 3AG, United Kingdom',
      addressLine1: 'Oxford Circus',
      addressLine3: 'Oxford Street, England',
      town: 'London',
      county: 'London',
      postCode: 'W1B 3AG',
    }),
    where: {
      latitude: 51.5151,
      longitude: -0.1419,
    },
    vehicleDetails: vehicleCatalog.hybrid,
    driver: createDriver(
      'Oliver Pope',
      '+447700900206',
      'Kia Niro',
      'HN21 CAB',
      'White',
    ),
    status: {
      progress: 'Passenger_Dropped_Off',
      status: 'In_Progress',
      ragStatus: 'Green',
      estimatedArrivalTime: 0,
    },
    estimatedPrice: createPrice(41),
  }),
  createJob({
    id: 9107,
    bookingId: 6207,
    jobReference: 'J00009107',
    bookingReference: 'B00046207',
    journeyReference: 'C00046207',
    when: isoMinutesFromNow(25),
    estimatedArrivalTime: 32,
    isImmediateJourney: true,
    passengers: [
      createPassenger('Harper Ellis', true, '+447700900107'),
      createPassenger('Leo Ellis'),
    ],
    pickup: createAddress({
      latitude: 51.5099,
      longitude: -0.1181,
      formattedAddress: 'Somerset House, London, WC2R 1LA, United Kingdom',
      addressLine1: 'Somerset House',
      addressLine3: 'Strand, England',
      town: 'London',
      county: 'London',
      postCode: 'WC2R 1LA',
    }),
    dropOff: createAddress({
      latitude: 51.5113,
      longitude: -0.0904,
      formattedAddress: 'Bank Station, London, EC3V 3LA, United Kingdom',
      addressLine1: 'Bank Station',
      addressLine3: 'City of London, England',
      town: 'London',
      county: 'London',
      postCode: 'EC3V 3LA',
    }),
    vehicleDetails: vehicleCatalog.standard,
    status: {
      progress: 'Confirmed',
      status: 'Not_Dispatched',
      ragStatus: 'Flashing',
      estimatedArrivalTime: 0,
    },
    estimatedPrice: createPrice(34),
  }),
  createJob({
    id: 9108,
    bookingId: 6208,
    jobReference: 'J00009108',
    bookingReference: 'B00046208',
    journeyReference: 'C00046208',
    when: isoMinutesFromNow(75),
    estimatedArrivalTime: 46,
    passengers: [createPassenger('Grace Howard', true, '+447700900108')],
    pickup: createAddress({
      latitude: 51.47,
      longitude: -0.4543,
      formattedAddress: 'Terminal 2, Heathrow Airport, TW6 1EW, United Kingdom',
      addressLine1: 'Terminal 2',
      addressLine3: 'Heathrow, England',
      town: 'Hounslow',
      county: 'Greater London',
      postCode: 'TW6 1EW',
    }),
    dropOff: createAddress({
      latitude: 51.5202,
      longitude: -0.1823,
      formattedAddress: '221B Baker Street, London, NW1 6XE, United Kingdom',
      addressLine1: '221B Baker Street',
      addressLine3: 'Marylebone, England',
      town: 'London',
      county: 'London',
      postCode: 'NW1 6XE',
    }),
    vehicleDetails: vehicleCatalog.executive,
    services: [{ serviceTypeName: 'MeetAndGreet', quantity: 1 }],
    flightDetails: {
      flightNumber: 'BA147',
    },
    status: {
      progress: 'Confirmed',
      status: 'Awaiting_Dispatch',
      ragStatus: 'Amber',
      estimatedArrivalTime: 0,
    },
    supplier: {
      supplierId: 19,
      companyName: 'Offline Supplier',
      supplierReference: '62000019',
      isDestinationLogicSupplier: true,
      isOffline: true,
      contactNumber: '+441612345678',
    },
    isOnwardJourney: true,
    estimatedPrice: createPrice(67),
  }),
  createJob({
    id: 9109,
    bookingId: 6209,
    jobReference: 'J00009109',
    bookingReference: 'B00046209',
    journeyReference: 'C00046209',
    when: isoMinutesFromNow(180),
    estimatedArrivalTime: 55,
    passengers: [createPassenger('Sophie Bennett', true, '+447700900109')],
    client: {
      clientId: 4,
      accountNumber: '00001004',
      companyName: 'Acme Travel UK',
    },
    supplier: {
      supplierId: 19,
      companyName: 'iCabbi',
      supplierReference: '62000019',
      isDestinationLogicSupplier: true,
      contactNumber: '+441612345678',
    },
    pickup: createAddress({
      latitude: 53.4808,
      longitude: -2.2426,
      formattedAddress:
        '1 St Peters Square, Manchester, M2 3DE, United Kingdom',
      addressLine1: '1 St Peters Square',
      addressLine3: 'England',
      town: 'Manchester',
      county: 'Greater Manchester',
      postCode: 'M2 3DE',
    }),
    dropOff: createAddress({
      latitude: 53.3658,
      longitude: -2.2727,
      formattedAddress: 'Manchester Airport, M90 1QX, United Kingdom',
      addressLine1: 'Manchester Airport',
      addressLine3: 'England',
      town: 'Manchester',
      county: 'Greater Manchester',
      postCode: 'M90 1QX',
    }),
    vehicleDetails: vehicleCatalog.premium,
    status: {
      progress: 'Confirmed',
      status: 'Unallocated',
      ragStatus: 'Amber',
      estimatedArrivalTime: 0,
    },
    estimatedPrice: createPrice(79),
  }),
  createJob({
    id: 9110,
    bookingId: 6210,
    jobReference: 'J00009110',
    bookingReference: 'B00046210',
    journeyReference: 'C00046210',
    when: londonNow().minus({ days: 2, hours: 1 }).toISO(),
    estimatedArrivalTime: 20,
    passengers: [createPassenger('Maya Walker', true, '+447700900110')],
    client: {
      clientId: 7,
      accountNumber: '00001007',
      companyName: 'Northwind Mobility',
      alias: 'Northwind',
    },
    pickup: createAddress({
      latitude: 51.4545,
      longitude: -2.5879,
      formattedAddress: 'Temple Meads, Bristol, BS1 6QF, United Kingdom',
      addressLine1: 'Temple Meads',
      addressLine3: 'England',
      town: 'Bristol',
      county: 'Bristol',
      postCode: 'BS1 6QF',
    }),
    dropOff: createAddress({
      latitude: 51.4501,
      longitude: -2.5794,
      formattedAddress: 'College Green, Bristol, BS1 5TR, United Kingdom',
      addressLine1: 'College Green',
      addressLine3: 'England',
      town: 'Bristol',
      county: 'Bristol',
      postCode: 'BS1 5TR',
    }),
    status: {
      progress: 'Manual_Intervention_Required',
      status: 'Attention_Needed',
      ragStatus: 'Red',
      estimatedArrivalTime: 0,
      statusNote: 'Stale intervention case for hide/show testing.',
    },
    estimatedPrice: createPrice(31),
  }),
  createJob({
    id: 9111,
    bookingId: 6211,
    jobReference: 'J00009111',
    bookingReference: 'B00046211',
    journeyReference: 'C00046211',
    when: isoMinutesFromNow(40),
    estimatedArrivalTime: 24,
    passengers: [createPassenger('Ava Sinclair', true, '+447700900111')],
    pickup: createAddress({
      latitude: 55.9533,
      longitude: -3.1883,
      formattedAddress: 'Waverley Bridge, Edinburgh, EH1 1BB, United Kingdom',
      addressLine1: 'Waverley Bridge',
      addressLine3: 'Scotland',
      town: 'Edinburgh',
      county: 'Edinburgh',
      postCode: 'EH1 1BB',
    }),
    dropOff: createAddress({
      latitude: 55.9444,
      longitude: -3.1883,
      formattedAddress: 'The Meadows, Edinburgh, EH8 9QE, United Kingdom',
      addressLine1: 'The Meadows',
      addressLine3: 'Scotland',
      town: 'Edinburgh',
      county: 'Edinburgh',
      postCode: 'EH8 9QE',
    }),
    status: {
      progress: 'Manual_Intervention_Required',
      status: 'Attention_Needed',
      ragStatus: 'Red',
      estimatedArrivalTime: 0,
      statusNote: 'Supplier asked for manual confirmation before dispatch.',
    },
    estimatedPrice: createPrice(33),
  }),
  createJob({
    id: 9112,
    bookingId: 6212,
    jobReference: 'J00009112',
    bookingReference: 'B00046212',
    journeyReference: 'C00046212',
    when: isoMinutesFromNow(18),
    estimatedArrivalTime: 27,
    passengers: [createPassenger('Jack Foster', true, '+447700900112')],
    pickup: createAddress({
      latitude: 52.4862,
      longitude: -1.8904,
      formattedAddress: 'Snow Hill, Birmingham, B3 2BJ, United Kingdom',
      addressLine1: 'Snow Hill',
      addressLine3: 'England',
      town: 'Birmingham',
      county: 'West Midlands',
      postCode: 'B3 2BJ',
    }),
    dropOff: createAddress({
      latitude: 52.4751,
      longitude: -1.9196,
      formattedAddress: 'Broad Street, Birmingham, B1 2EA, United Kingdom',
      addressLine1: 'Broad Street',
      addressLine3: 'England',
      town: 'Birmingham',
      county: 'West Midlands',
      postCode: 'B1 2EA',
    }),
    status: {
      progress: 'Manual_Reallocation_Pending',
      status: 'Attention_Needed',
      ragStatus: 'Red',
      estimatedArrivalTime: 0,
      statusNote:
        'Primary supplier dropped the booking. Manual fallback running.',
    },
    estimatedPrice: createPrice(37),
  }),
  createJob({
    id: 9113,
    bookingId: 6213,
    jobReference: 'J00009113',
    bookingReference: 'B00046213',
    journeyReference: 'C00046213',
    when: isoMinutesFromNow(55),
    estimatedArrivalTime: 34,
    passengers: [createPassenger('Ella Hughes', true, '+447700900113')],
    pickup: createAddress({
      latitude: 51.752,
      longitude: -1.2577,
      formattedAddress: 'Oxford Station, Oxford, OX1 1HS, United Kingdom',
      addressLine1: 'Oxford Station',
      addressLine3: 'England',
      town: 'Oxford',
      county: 'Oxfordshire',
      postCode: 'OX1 1HS',
    }),
    dropOff: createAddress({
      latitude: 51.7548,
      longitude: -1.2544,
      formattedAddress: 'High Street, Oxford, OX1 4AJ, United Kingdom',
      addressLine1: 'High Street',
      addressLine3: 'England',
      town: 'Oxford',
      county: 'Oxfordshire',
      postCode: 'OX1 4AJ',
    }),
    status: {
      progress: 'Primary_Reallocation_Pending',
      status: 'Attention_Needed',
      ragStatus: 'Amber',
      estimatedArrivalTime: 0,
    },
    estimatedPrice: createPrice(29),
  }),
  createJob({
    id: 9114,
    bookingId: 6214,
    jobReference: 'J00009114',
    bookingReference: 'B00046214',
    journeyReference: 'C00046214',
    when: isoMinutesFromNow(12),
    estimatedArrivalTime: 21,
    passengers: [createPassenger('Freya Sullivan', true, '+447700900114')],
    pickup: createAddress({
      latitude: 53.4084,
      longitude: -2.9916,
      formattedAddress:
        'Liverpool Lime Street, Liverpool, L1 1JD, United Kingdom',
      addressLine1: 'Liverpool Lime Street',
      addressLine3: 'England',
      town: 'Liverpool',
      county: 'Merseyside',
      postCode: 'L1 1JD',
    }),
    dropOff: createAddress({
      latitude: 53.4045,
      longitude: -2.9779,
      formattedAddress: 'Royal Albert Dock, Liverpool, L3 4AF, United Kingdom',
      addressLine1: 'Royal Albert Dock',
      addressLine3: 'England',
      town: 'Liverpool',
      county: 'Merseyside',
      postCode: 'L3 4AF',
    }),
    status: {
      progress: 'Booking_Update_Pending',
      status: 'Attention_Needed',
      ragStatus: 'Amber',
      estimatedArrivalTime: 0,
      statusNote:
        'Passenger updated notes while dispatch request is in flight.',
    },
    estimatedPrice: createPrice(27),
  }),
  createJob({
    id: 9115,
    bookingId: 6215,
    jobReference: 'J00009115',
    bookingReference: 'B00046215',
    journeyReference: 'C00046215',
    when: isoMinutesFromNow(-35),
    estimatedArrivalTime: 15,
    passengers: [createPassenger('Theo Barnes', true, '+447700900115')],
    pickup: createAddress({
      latitude: 51.4816,
      longitude: -3.1791,
      formattedAddress: 'Cardiff Central, Cardiff, CF10 1EP, United Kingdom',
      addressLine1: 'Cardiff Central',
      addressLine3: 'Wales',
      town: 'Cardiff',
      county: 'South Glamorgan',
      postCode: 'CF10 1EP',
    }),
    dropOff: createAddress({
      latitude: 51.4862,
      longitude: -3.1689,
      formattedAddress: 'Civic Centre, Cardiff, CF10 3ND, United Kingdom',
      addressLine1: 'Civic Centre',
      addressLine3: 'Wales',
      town: 'Cardiff',
      county: 'South Glamorgan',
      postCode: 'CF10 3ND',
    }),
    where: {
      latitude: 51.4816,
      longitude: -3.1791,
    },
    vehicleDetails: vehicleCatalog.standard,
    driver: createDriver(
      'Rhys Morgan',
      '+447700900215',
      'Skoda Octavia',
      'CF19 TAX',
      'White',
    ),
    status: {
      progress: 'No_Show',
      status: 'Attention_Needed',
      ragStatus: 'Red',
      estimatedArrivalTime: 0,
      statusNote: 'Driver waited 10 minutes and could not reach passenger.',
    },
    estimatedPrice: createPrice(24),
  }),
  createJob({
    id: 9116,
    bookingId: 6216,
    jobReference: 'J00009116',
    bookingReference: 'B00046216',
    journeyReference: 'C00046216',
    journeyTypeName: 'Flexible',
    when: isoMinutesFromNow(95),
    estimatedArrivalTime: 80,
    passengers: [
      createPassenger('Ruby Gibson', true, '+447700900116'),
      createPassenger('Archie Gibson'),
      createPassenger('Lily Gibson'),
    ],
    pickup: createAddress({
      latitude: 51.5079,
      longitude: -0.0877,
      formattedAddress: 'Leadenhall Street, London, EC3V 4AB, United Kingdom',
      addressLine1: 'Leadenhall Street',
      addressLine3: 'City of London, England',
      town: 'London',
      county: 'London',
      postCode: 'EC3V 4AB',
    }),
    additionalStops: [
      createAddress({
        latitude: 51.5117,
        longitude: -0.1031,
        formattedAddress: 'Holborn Circus, London, EC1N 2HT, United Kingdom',
        addressLine1: 'Holborn Circus',
        addressLine3: 'Holborn, England',
        town: 'London',
        county: 'London',
        postCode: 'EC1N 2HT',
      }),
      createAddress({
        latitude: 51.5184,
        longitude: -0.0817,
        formattedAddress:
          'Shoreditch High Street, London, E1 6PJ, United Kingdom',
        addressLine1: 'Shoreditch High Street',
        addressLine3: 'Shoreditch, England',
        town: 'London',
        county: 'London',
        postCode: 'E1 6PJ',
      }),
    ],
    dropOff: createAddress({
      latitude: 51.5225,
      longitude: -0.1101,
      formattedAddress: 'Rosebery Avenue, London, EC1R 4RP, United Kingdom',
      addressLine1: 'Rosebery Avenue',
      addressLine3: 'Clerkenwell, England',
      town: 'London',
      county: 'London',
      postCode: 'EC1R 4RP',
    }),
    vehicleDetails: vehicleCatalog.mpv,
    services: [{ serviceTypeName: 'MeetAndGreet', quantity: 1 }],
    flightDetails: {
      flightNumber: 'N/A',
    },
    status: {
      progress: 'Confirmed',
      status: 'Not_Dispatched',
      ragStatus: 'Amber',
      estimatedArrivalTime: 0,
    },
    estimatedPrice: createPrice(73),
  }),
  createJob({
    id: 9117,
    bookingId: 6217,
    jobReference: 'J00009117',
    bookingReference: 'B00046217',
    journeyReference: 'C00046217',
    journeyTypeName: 'Return',
    when: isoMinutesFromNow(300),
    estimatedArrivalTime: 40,
    associatedBookings: [6218],
    passengers: [createPassenger('Hugo Turner', true, '+447700900117')],
    pickup: createAddress({
      latitude: 52.9548,
      longitude: -1.1581,
      formattedAddress: 'Nottingham Station, NG2 3AQ, United Kingdom',
      addressLine1: 'Nottingham Station',
      addressLine3: 'England',
      town: 'Nottingham',
      county: 'Nottinghamshire',
      postCode: 'NG2 3AQ',
    }),
    dropOff: createAddress({
      latitude: 52.9383,
      longitude: -1.2076,
      formattedAddress:
        'East Midlands Conference Centre, NG7 2RJ, United Kingdom',
      addressLine1: 'East Midlands Conference Centre',
      addressLine3: 'England',
      town: 'Nottingham',
      county: 'Nottinghamshire',
      postCode: 'NG7 2RJ',
    }),
    vehicleDetails: vehicleCatalog.untrackedPremium,
    status: {
      progress: 'Confirmed',
      status: 'Not_Dispatched',
      ragStatus: 'Flashing',
      estimatedArrivalTime: 0,
    },
    estimatedPrice: createPrice(61),
  }),
  createJob({
    id: 9118,
    bookingId: 6218,
    parentBookingId: 6217,
    jobReference: 'J00009118',
    bookingReference: 'B00046217',
    journeyReference: 'C00046218',
    journeyTypeName: 'Return',
    when: isoMinutesFromNow(720),
    estimatedArrivalTime: 42,
    passengers: [createPassenger('Hugo Turner', true, '+447700900117')],
    pickup: createAddress({
      latitude: 52.9383,
      longitude: -1.2076,
      formattedAddress:
        'East Midlands Conference Centre, NG7 2RJ, United Kingdom',
      addressLine1: 'East Midlands Conference Centre',
      addressLine3: 'England',
      town: 'Nottingham',
      county: 'Nottinghamshire',
      postCode: 'NG7 2RJ',
    }),
    dropOff: createAddress({
      latitude: 52.9548,
      longitude: -1.1581,
      formattedAddress: 'Nottingham Station, NG2 3AQ, United Kingdom',
      addressLine1: 'Nottingham Station',
      addressLine3: 'England',
      town: 'Nottingham',
      county: 'Nottinghamshire',
      postCode: 'NG2 3AQ',
    }),
    vehicleDetails: vehicleCatalog.untrackedPremium,
    status: {
      progress: 'Confirmed',
      status: 'Not_Dispatched',
      ragStatus: 'Flashing',
      estimatedArrivalTime: 0,
    },
    estimatedPrice: createPrice(61),
  }),
]

const getLeadPassengerName = (job: any) =>
  job?.passengers?.find((passenger: any) => passenger.isLeadPassenger)?.name ??
  ''

const getSearchHaystack = (job: any) =>
  [
    job?.jobReference,
    job?.bookingReference,
    job?.journeyReference,
    job?.client?.companyName,
    job?.supplier?.companyName,
    job?.supplier?.supplierReference,
    getLeadPassengerName(job),
    job?.pickup?.formattedAddress,
    job?.dropOff?.formattedAddress,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

const compareValues = (left: string | number, right: string | number) => {
  if (typeof left === 'number' && typeof right === 'number') {
    return left - right
  }

  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

const sortJobs = (jobs: any[], orderBy?: MockSearchPayload['orderBy']) => {
  const direction = orderBy?.isDescending ? -1 : 1
  const column = orderBy?.jobOrderByTypeName ?? 'When'

  return [...jobs].sort((a, b) => {
    switch (column) {
      case 'JobReference':
        return compareValues(a.jobReference, b.jobReference) * direction
      case 'PassengerName':
        return (
          compareValues(getLeadPassengerName(a), getLeadPassengerName(b)) *
          direction
        )
      case 'ClientName':
        return (
          compareValues(a.client?.companyName, b.client?.companyName) *
          direction
        )
      case 'Progress':
        return compareValues(a.status?.progress, b.status?.progress) * direction
      case 'Status':
        return compareValues(a.status?.status, b.status?.status) * direction
      case 'When':
      default:
        return (
          compareValues(
            DateTime.fromISO(String(a.when)).toMillis(),
            DateTime.fromISO(String(b.when)).toMillis(),
          ) * direction
        )
    }
  })
}

const filterJobs = (jobs: any[], payload?: MockSearchPayload) => {
  const searchTerm = String(payload?.search ?? '')
    .trim()
    .toLowerCase()
  const statusFilters = payload?.status ?? []
  const clientName = String(payload?.clientName ?? '')
    .trim()
    .toLowerCase()

  return jobs.filter((job) => {
    if (
      statusFilters.length > 0 &&
      !statusFilters.includes(String(job?.status?.status))
    ) {
      return false
    }

    if (
      clientName &&
      String(job?.client?.companyName ?? '')
        .trim()
        .toLowerCase() !== clientName
    ) {
      return false
    }

    if (searchTerm && !getSearchHaystack(job).includes(searchTerm)) {
      return false
    }

    return true
  })
}

export const getMockBookingStatusPage = ({
  limit,
  offset = 0,
  payload,
}: MockSearchArgs) => {
  const filteredJobs = filterJobs(mockJobs, payload)
  const sortedJobs = sortJobs(filteredJobs, payload?.orderBy)
  const safeOffset = Math.max(0, offset)
  const safeLimit = Math.max(1, limit)
  const value = sortedJobs.slice(safeOffset, safeOffset + safeLimit)

  return {
    offset: safeOffset,
    limit: safeLimit,
    size: filteredJobs.length,
    first: {
      href: 'mock://booking-status/jobs/search',
      rel: ['collection'],
    },
    next:
      safeOffset + safeLimit < filteredJobs.length
        ? {
            href: `mock://booking-status/jobs/search?offset=${
              safeOffset + safeLimit
            }&limit=${safeLimit}`,
            rel: ['collection'],
          }
        : null,
    last: {
      href: `mock://booking-status/jobs/search?offset=${Math.max(
        Math.floor((filteredJobs.length - 1) / safeLimit) * safeLimit,
        0,
      )}&limit=${safeLimit}`,
      rel: ['collection'],
    },
    value,
    href: 'mock://booking-status/jobs/search',
    rel: ['collection'],
  }
}

export const getMockBookingStatusJobById = (id: number | string) =>
  mockJobs.find((job) => String(job.id) === String(id))

export const getMockBookingStatusJobResponse = (id: number | string) => {
  const job = getMockBookingStatusJobById(id)
  if (!job) {
    return undefined
  }

  return {
    data: job,
    status: 200,
    statusText: 'OK',
    headers: {},
    config: {},
  }
}
